import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  SortingState,
  PaginationState,
} from "@tanstack/react-table";
import { Image } from "react-bootstrap";
import cn from "classnames";
import React, { useEffect, useMemo, useState } from "react";
import UserCard from "../../../../../../../component/common/UserCard/UserCard";
import { ASSESTS } from "../../../../../../../constant/assest";
import CustomButton, {
  ButtonFont,
  ButtonSize,
  ButtonType,
} from "../../../../../../../component/common/CustomButton/CustomButton";
import Pagination from "../../../../../../../component/UI/Pagination/Pagination";
import TableSkeleton from "../../../../../../../component/common/TableSkeleton";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../../../../redux/store";
import { fetchUserReferrals } from "../../../../../../../redux/slices/portfolio/actions";
import { ReferralListItem } from "../../../../../../../redux/slices/portfolio/types";
import { usePagination } from "../../../../../../../hooks/usePagination";

interface ReferralsTableProps {
  userId: string;
}
const ReferralsTable: React.FC<ReferralsTableProps> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { referralList, referralListLoading } = useSelector(
    (state: RootState) => state.portfolio
  );

  const data = useMemo(() => referralList?.refList || [], [referralList]);
  const columns = useMemo<ColumnDef<ReferralListItem>[]>(
    () => [
      {
        header: "Invited Users",
        accessorKey: "invited_user.full_name",
        cell: (info) => {
          const row = info.row.original;
          return (
            <UserCard
              userName={row.invited_user.full_name || 'N/A'}
              userHandle={row.invited_user.user_name}
              userImage={row.invited_user.profile_image}
            />
          );
        },
        sortingFn: (rowA, rowB, columnId) => {
          const nameA = rowA.getValue<string | null>(columnId) || '';
          const nameB = rowB.getValue<string | null>(columnId) || '';

          if (!nameA && nameB) return -1;
          if (nameA && !nameB) return 1;
          if (!nameA && !nameB) return 0;

          return nameA.localeCompare(nameB);
        }
      },
      { 
        header: "Join Date", 
        accessorKey: "invited_user.joining_date", 
        cell: (info) => {
          const value = info.getValue<string>();
          return value ? value.split('T')[0] : 'N/A';
        },
      },
      { header: "Referral Date",
        accessorKey: "referral_date",
        cell: (info) => {
          const value = info.getValue<string>();
          return value ? value.split('T')[0] : 'N/A';
        },
      },
      {
        header: "Successful Referral",
        accessorKey: "referral_status",
        cell: (info) => {
          const value = info.getValue<string>();
          return (
            <span className={cn("subscribeBadge", value?.toLowerCase())}>
              {value}
            </span>
          );
        },
      },
      {
        header: "Subscription",
        accessorKey: "invited_user.subscriptions",
        cell: (info) => {
          const value = info.getValue<string>();
          return (
            <span className={cn("statusBadge", value?.toLowerCase())}>
              {value}
            </span>
          );
        },
      },
    ],
    []
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  });

  useEffect(() => {
    if (userId) {
      dispatch(
        fetchUserReferrals({ userId, page: pageIndex + 1, limit: pageSize })
      );
    }
  }, [dispatch, userId, pageIndex, pageSize]);

  const pageCount = referralList?.totalPages || 0;
  const totalEntries = referralList?.total || 0;

  const table = useReactTable({
    data,
    columns,
    state: { sorting, pagination: { pageIndex, pageSize } },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    sortDescFirst: false,
    manualPagination: true,
    pageCount: pageCount,
    initialState: { pagination: { pageIndex: 0, pageSize: pageSize } },
  });

   const paginationGroup = usePagination({
      currentPageIndex: pageIndex,
      totalPages: pageCount,
    });
  
    const pageNumbersElements = paginationGroup.map((page, index) => {
      if (typeof page === 'string') {
        return <span key={`ellipsis-${index}`} className={'pageNumberBtn'} style={{cursor: 'default'}}>{page}</span>;
      }
  
      const pIndex = page;
      return (
        <CustomButton
          key={pIndex}
          onClick={() => table.setPageIndex(pIndex)}
          disabled={pageIndex === pIndex}
          btnType={
            pageIndex === pIndex
              ? ButtonType.ACTIVENOSTYLE
              : ButtonType.NOSTYLE
          }
          btnSize={ButtonSize.NORMAL}
          btnFont={
            pageIndex === pIndex
              ? ButtonFont.BOLD
              : ButtonFont.NORMAL
          }
          className={cn(
            'pageNumberBtn',
            pageIndex === pIndex && 'activePageNumberBtn'
          )}
        >
          {pIndex + 1}
        </CustomButton>
      );
    });

  return (
    <div className={'tableContainer'}>
      <div className={"tableWrapper"}>
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    style={{
                      cursor: header.column.getCanSort()
                        ? "pointer"
                        : "default",
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getCanSort() && (
                      <span className={cn("tableShorting")}>
                        <Image
                          src={
                            header.column.getIsSorted() === "asc"
                              ? ASSESTS.ASSENDING
                              : header.column.getIsSorted() === "desc"
                              ? ASSESTS.DECENDING
                              : ASSESTS.SHORT
                          }
                          alt="Short"
                        />
                      </span>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {referralListLoading === "pending" ? (
              <TableSkeleton rows={pageSize} columns={columns.length} />
            ) : totalEntries === 0 ? (
               <tr>
                  <td colSpan={columns.length} className={'noResultsFound'}>
                    No results found
                  </td>
                </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        enteriesShows={`${
          totalEntries ? pageIndex * pageSize + 1 : 0
        }-${Math.min(
          (pageIndex + 1) * pageSize,
          totalEntries
        )} of ${totalEntries} Entries`}

        pageNumbers= {[
          pageCount > 1 && (
            <CustomButton
              key="prev"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              btnType={ButtonType.NOSTYLE}
              className={'pageNumberBtn'}
            >
              Prev
            </CustomButton>
          ),
          ...pageNumbersElements,
          pageCount > 1 && (
            <CustomButton
              key="next"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              btnType={ButtonType.NOSTYLE}
              className={'pageNumberBtn'}
            >
              Next
            </CustomButton>
          )
        ].filter(Boolean)}
        dropDownOptions={[
          { value: "5", label: "5" },
          { value: "10", label: "10" },
          { value: "20", label: "20" },
          { value: "25", label: "25" },
        ]}
        dropdownOnChange={(value) => {
          setPagination((old) => ({
            ...old,
            pageSize: Number(value),
            pageIndex: 0,
          }));
        }}
        dropDownPlaceholder={pageSize.toString()}
      />
    </div>
  );
};

export default React.memo(ReferralsTable);
